/**
 * versionCheckService.js — detects when the running front-end build is not the
 * one currently deployed.
 *
 * Flow:
 *   1. Compare BUILD_VERSION (baked into THIS bundle at build time) against the
 *      id served by /version.json.
 *   2. Do that on boot, on a poll, and whenever the tab regains focus.
 *   3. On any mismatch, invoke onUpdateAvailable so the UI can prompt a reload.
 *
 * ⚠️ WHY THE COMPARISON IS AGAINST THE BAKED-IN VERSION (regression 2026-07-26):
 * this previously recorded /version.json AT BOOT and compared later fetches to
 * that. It therefore detected "a deploy happened while your tab was open" but
 * was structurally blind to "the code I am running is ALREADY stale" — a
 * browser served an old bundle out of the service-worker precache would boot,
 * read the NEW version.json, adopt the new id as its own baseline, and never
 * report an update. Users sat on stale bundles until they hit a hard failure
 * (a deleted route chunk → blank page). Comparing the running build to the
 * served build covers both cases with one rule. Do not reintroduce a
 * boot-time baseline.
 *
 * Why polling (not the service worker lifecycle): preact-cli registers its
 * Workbox service worker internally, so update timing is not ours to control.
 * Polling a tiny version file is framework-agnostic.
 *
 * /version.json is written AFTER the webpack build (see scripts/gen-version.js),
 * so it is never part of the precache manifest. It is fetched with a cache
 * buster + `no-store`, and is served `must-revalidate` (see _headers).
 *
 * ⚠️ ADOPTING THE NEW BUILD (toast storm, 2026-08-13): the prompt's reload
 * action must be adoptNewBuild(), never a bare window.location.reload(). The
 * service worker skipWaiting()s but deliberately does NOT clientsClaim() (see
 * src/sw.js), so a plain reload races the new worker's install: the OLD worker
 * still controls the navigation and serves the OLD precached /200.html — the
 * stale bundle boots again, the boot check fires again, and the toast
 * reappears as if the reload did nothing. Rapid re-clicks abort and restart
 * the install, looping indefinitely. adoptNewBuild() waits (bounded) for the
 * fresh worker to activate BEFORE navigating, and leaves a one-shot
 * sessionStorage marker so a boot that still lands stale retries silently
 * once instead of re-prompting. The marker is consumed before the retry, so a
 * persistently-stale client falls back to prompting — never an automatic
 * reload loop. (This is distinct from the controllerchange auto-reload that
 * sw.js forbids: that fires mid-session and wipes unsaved input; this runs
 * only within seconds of the user's own reload click.)
 */

import { updateServiceWorkerAndWait, nudgeServiceWorkerUpdate } from '../utils/swUpdate';

const VERSION_URL = '/version.json';
const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

// One-shot marker: "the last navigation was a reload we triggered to adopt a
// new build". sessionStorage on purpose (scoped to the tab, survives the
// reload, gone when the tab closes); the age cap keeps a marker abandoned by
// an interrupted reload from triggering a surprise auto-reload much later.
const RELOAD_MARKER_KEY = 'kegtron_update_reload_pending';
const RELOAD_MARKER_MAX_AGE_MS = 60 * 1000;

/**
 * Literal replaced in the built bundles by scripts/gen-version.js. It stays
 * unreplaced under `preact watch`, which is the signal to disable the check
 * locally rather than raise a false "update available".
 */
// NOTE: this literal must appear EXACTLY ONCE in this file. Two copies let the
// minifier constant-fold a comparison between them, dead-code-eliminate the
// branch, and drop the string entirely — which silently disables update
// detection. (gen-version.js exits non-zero if it cannot find it, which is how
// that was caught.) "Unreplaced" is therefore detected by shape, not equality.
const BUILD_VERSION = '__KT_BUILD_VERSION__';

/** Matches a build id that is still the unsubstituted placeholder. */
const UNREPLACED_RE = /^__[A-Z0-9_]+__$/;

/**
 * True when the running bundle is not the build the server is serving.
 * Versions are opaque strings — a rollback is a change worth adopting too.
 *
 * @param {string|null|undefined} buildVersion  version baked into this bundle
 * @param {string|null|undefined} servedVersion version from /version.json
 * @returns {boolean}
 */
export function isStaleBuild(buildVersion, servedVersion) {
  if (!buildVersion || !servedVersion) return false;
  if (UNREPLACED_RE.test(buildVersion)) return false; // dev server
  return buildVersion !== servedVersion;
}

let intervalId = null;
let visibilityHandler = null;
let started = false;
let notified = false;
let checkInFlight = false;

function setReloadMarker() {
  try {
    window.sessionStorage.setItem(RELOAD_MARKER_KEY, String(Date.now()));
  } catch (err) {
    // Private mode / storage disabled: we lose the silent retry, not the reload.
  }
}

/**
 * Read AND clear the reload marker. True only when a fresh marker was present
 * — i.e. this boot is the direct result of an adoptNewBuild() reload.
 */
function consumeReloadMarker() {
  try {
    const raw = window.sessionStorage.getItem(RELOAD_MARKER_KEY);
    if (!raw) return false;
    window.sessionStorage.removeItem(RELOAD_MARKER_KEY);
    const age = Date.now() - Number(raw);
    return Number.isFinite(age) && age >= 0 && age <= RELOAD_MARKER_MAX_AGE_MS;
  } catch (err) {
    return false;
  }
}

/**
 * Reload onto the currently-deployed build, reliably. Waits (bounded) for the
 * freshly-installing service worker to activate so the reload navigation is
 * served by the NEW worker's precache instead of the old one — see the header
 * comment for why a bare location.reload() is not enough. This is the ONLY
 * reload the update prompt should ever perform.
 */
export async function adoptNewBuild() {
  setReloadMarker();
  await updateServiceWorkerAndWait();
  window.location.reload();
}

async function fetchVersion() {
  try {
    const res = await fetch(`${VERSION_URL}?ts=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json();
    return data && data.version ? String(data.version) : null;
  } catch (err) {
    // Network hiccup — treat as "unknown", never as an update.
    return null;
  }
}

/**
 * Begin watching for new deployments.
 * @param {() => void} onUpdateAvailable - called once when a newer build is detected
 */
export async function initVersionCheck(onUpdateAvailable, buildVersionOverride) {
  if (started || typeof window === 'undefined') return;
  started = true;

  // buildVersionOverride exists so tests can supply a running-build id; the
  // real value is substituted into the bundle at build time and cannot be
  // varied from a test. Production never passes it.
  const runningVersion = buildVersionOverride || BUILD_VERSION;

  // Unreplaced placeholder = dev server; no meaningful comparison to make.
  if (UNREPLACED_RE.test(runningVersion)) {
    started = false;
    return;
  }

  // Consume the marker unconditionally at boot — whether or not this boot
  // turns out stale — so a marker from a SUCCESSFUL adoption can never linger
  // and trigger a surprise auto-reload later in the session.
  const isAdoptionReloadBoot = consumeReloadMarker();

  // Boot nudge: browsers only soft-update the service worker when the
  // registration is >24 h stale, so plain navigations sail straight past a
  // new deploy (see swUpdate.js). Kicking an explicit check here means a tab
  // that boots under an old worker starts converging immediately — by the
  // time the prompt (or the adoptNewBuild wait) runs, the fresh worker is
  // typically already active. Fire-and-forget; never blocks boot.
  nudgeServiceWorkerUpdate();

  // `notified` alone cannot make this re-entrant-safe: it is only assigned
  // AFTER the fetch await, so overlapping callers (poll + visibilitychange,
  // which fires on every focus regain) can each clear the guard before any of
  // them sets it, and each would then fire onUpdateAvailable. `checkInFlight`
  // latches synchronously, before the first suspension point, so at most one
  // check is ever in flight. Kept out of isStaleBuild, which stays pure.
  //
  // `allowSilentRetry` is true ONLY for the boot check, and only when this
  // boot is itself the result of an adoptNewBuild() reload that still landed
  // on the stale bundle (activation lost the race). In that case the user
  // JUST clicked reload — finishing their request beats re-prompting them.
  // The marker is already consumed, so the retry cannot loop: a boot that is
  // stale for a third time prompts again. Later polls never pass it — a
  // deploy five minutes after our reload is a NEW deploy that deserves a
  // prompt.
  const check = async (allowSilentRetry = false) => {
    if (notified || checkInFlight) return;
    checkInFlight = true;
    try {
      const served = await fetchVersion();
      // Compare against the build we are RUNNING, never a boot-time snapshot —
      // that is what makes an already-stale bundle detectable at all.
      if (isStaleBuild(runningVersion, served)) {
        notified = true;
        stopVersionCheck();
        if (allowSilentRetry) {
          await updateServiceWorkerAndWait();
          window.location.reload();
          return;
        }
        // Pre-warm the new worker while the prompt is on screen, so the
        // user's eventual click adopts instantly instead of waiting out the
        // install inside adoptNewBuild().
        nudgeServiceWorkerUpdate();
        if (typeof onUpdateAvailable === 'function') onUpdateAvailable();
      }
    } finally {
      checkInFlight = false;
    }
  };

  // Boot check: catches the tab that started up already stale.
  await check(isAdoptionReloadBoot);
  if (notified) return;

  intervalId = setInterval(check, POLL_INTERVAL_MS);
  visibilityHandler = () => {
    if (document.visibilityState === 'visible') check();
  };
  window.addEventListener('visibilitychange', visibilityHandler);
}

/**
 * Stop watching and clean up listeners/timers.
 */
export function stopVersionCheck() {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
  if (visibilityHandler) {
    window.removeEventListener('visibilitychange', visibilityHandler);
    visibilityHandler = null;
  }
}
